<template>
  <div class="roles-container">
    <!-- 面包屑 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/admin/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>人员管理</el-breadcrumb-item>
      <el-breadcrumb-item>角色管理</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 卡片 -->
    <el-card>
      <!-- 角色列表表格 -->
      <div class="table-scroll-wrapper">
        <el-table 
          :data="filteredRoleList" 
          border 
          stripe 
          :header-cell-style="{ background: '#F5F7FA', color: '#606266', fontWeight: '500' }">
        <el-table-column type='expand' width='50' align="center">
          <template v-slot='scope'>
            <div class="expand-content">
              <div class="permissions-container">
                <div 
                  v-for="(item1, i1) in getGroupedPermissionsByRole(scope.row.id)" 
                  :key="i1"
                  class="permission-group"
                  :class="{ 'first-group': i1 === 0 }">
                  <!-- 模块名称和权限列表在同一行 -->
                  <div class="module-row">
                    <div class="module-header">
                      <el-tag 
                        type="info" 
                        size="medium" 
                        closable 
                        @close="removePermissionFromRole(scope.row, item1.module)"
                        class="module-tag">
                        {{ item1.module }}
                      </el-tag>
                      <i class="el-icon-caret-right module-icon"></i>
                    </div>
                    <!-- 权限列表 -->
                    <div class="permissions-list">
                      <el-tag 
                        type='success' 
                        size="small" 
                        closable
                        @close="removePermissionFromRole(scope.row, null, item2.id)"
                        class="permission-tag"
                        v-for="item2 in item1.permissions" 
                        :key="item2.id">
                        {{ item2.name }}
                      </el-tag>
                    </div>
                  </div>
                </div>
                <el-empty 
                  v-if="getRolePermissions(scope.row.id).length === 0" 
                  description="该角色暂无权限" 
                  class="empty-state">
                </el-empty>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
        <el-table-column prop="name" align="center" label="角色名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column label="角色描述" min-width="250" show-overflow-tooltip>
          <template v-slot="scope">
            <span class="role-desc">{{ getRoleDesc(scope.row.id) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="权限数量" width="130" align="center">
          <template v-slot="scope">
            <el-tag type="primary" size="small" class="permission-count-tag">{{ getRolePermissions(scope.row.id).length }} 项</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template v-slot='scope'>
            <el-button 
              type="warning" 
              icon="el-icon-setting" 
              size='small' 
              @click="showAssignPermissionDialog(scope.row)">
              分配权限
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      </div>
    </el-card>

    <!-- 分配权限对话框 -->
    <el-dialog 
      title="分配权限" 
      :visible.sync="showAssignDialog" 
      width="600px" 
      class="assign-dialog"
      @close="resetAssignForm">
      <div class="dialog-header" v-if="currentRole">
        <el-alert
          :title="`正在为「${currentRole.name}」分配权限`"
          type="info"
          :closable="false"
          show-icon>
        </el-alert>
      </div>
      
      <!-- 按模块分组的多选框 -->
      <div class="permissions-selector">
        <div 
          v-for="item in allPermissionsGroupedSorted" 
          :key="item.module" 
          class="module-section">
          <div class="module-title">
            <el-checkbox 
              :indeterminate="isIndeterminate(item.module)"
              :value="isCheckAll(item.module)"
              @change="handleCheckAllChange($event, item.module)"
              class="module-checkbox">
              <el-tag type="info" size="medium" class="module-label-tag">{{ item.module }}</el-tag>
            </el-checkbox>
          </div>
          <el-checkbox-group v-model="selectedPermissionIds" class="checkbox-group">
            <div class="permissions-grid">
              <el-checkbox 
                :label="permission.id" 
                class="permission-checkbox"
                v-for="permission in item.permissions" 
                :key="permission.id">
                <span class="permission-name">{{ permission.name }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>

        <el-empty 
          v-if="allPermissionList.length === 0" 
          description="暂无权限数据" 
          class="empty-permissions">
        </el-empty>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showAssignDialog = false" size="medium">取 消</el-button>
        <el-button type="primary" @click="saveRolePermissions" size="medium">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "Roles",
  data() {
    return {
      // 角色权限相关
      rolePermissionsMap: {}, // 存储每个角色的权限 { roleId: [permissions] }
      // 对话框相关
      showAssignDialog: false,
      currentRole: null, // 当前正在分配权限的角色
      // 所有权限列表（用于分配权限）
      allPermissionList: [],
      selectedPermissionIds: [],
      roleList: [
        { id: 1, name: '超级管理员' },
        { id: 2, name: '管理员' },
        { id: 3, name: '运营' },
        { id: 4, name: '客服' },
        { id: 5, name: '财务' },
        { id: 6, name: '带队老师' }
      ]
    }
  },
  computed: {
    // 过滤掉超级管理员的角色列表
    filteredRoleList() {
      return this.roleList.filter(role => role.id !== 1)
    },
    // 按模块分组的所有权限（用于分配权限对话框）
    allPermissionsGrouped() {
      const grouped = {}
      this.allPermissionList.forEach(permission => {
        if (!grouped[permission.module]) {
          grouped[permission.module] = []
        }
        grouped[permission.module].push(permission)
      })
      return grouped
    },
    // 按模块名称排序的模块列表（用于分配权限对话框）
    allPermissionsGroupedSorted() {
      const grouped = this.allPermissionsGrouped
      const sortedModules = Object.keys(grouped).sort((a, b) => {
        return a.localeCompare(b, 'zh-CN')
      })
      return sortedModules.map(module => ({
        module,
        permissions: grouped[module]
      }))
    }
  },
  created() {
    this.fetchAllPermissions()
    this.fetchAllRolesPermissions()
  },
  methods: {
    // 获取所有权限（用于分配权限对话框）
    async fetchAllPermissions() {
      try {
        const res = await this.$http({
          method: 'get',
          url: 'permissions'
        })
        
        // 处理不同的响应格式
        let data = []
        if (res.code === 200) {
          data = res.data || []
        } else if (res.success) {
          data = res.data || []
        } else if (Array.isArray(res)) {
          data = res
        } else if (res.data && Array.isArray(res.data)) {
          data = res.data
        } else {
          console.error('未知的响应格式:', res)
          return this.$message.error(res.message || '获取权限列表失败')
        }
        
        this.allPermissionList = data
      } catch (err) {
        console.error('获取权限列表错误:', err)
        this.$message.error(err.response?.data?.message || err.message || '获取权限列表失败')
      }
    },
    
    // 获取所有角色的权限
    async fetchAllRolesPermissions() {
      try {
        // 并行获取所有角色的权限（除了超级管理员）
        const roleIds = this.roleList.filter(r => r.id !== 1).map(r => r.id)
        const promises = roleIds.map(roleId => this.fetchRolePermissions(roleId))
        await Promise.all(promises)
      } catch (err) {
        console.error('获取角色权限错误:', err)
      }
    },
    
    // 获取指定角色的权限
    async fetchRolePermissions(roleId) {
      if (!roleId || roleId === 1) {
        this.$set(this.rolePermissionsMap, roleId, [])
        return
      }
      
      try {
        const res = await this.$http({
          method: 'get',
          url: `roles/${roleId}/permissions`
        })
        
        // 处理不同的响应格式
        let permissions = []
        if (res.code === 200) {
          if (res.data && res.data.permissions) {
            permissions = res.data.permissions || []
          } else if (Array.isArray(res.data)) {
            permissions = res.data
          } else {
            permissions = res.data || []
          }
        } else if (res.success) {
          if (res.data && res.data.permissions) {
            permissions = res.data.permissions || []
          } else if (Array.isArray(res.data)) {
            permissions = res.data
          } else {
            permissions = res.data || []
          }
        } else if (res.data && res.data.permissions) {
          permissions = res.data.permissions || []
        } else if (Array.isArray(res.data)) {
          permissions = res.data
        } else if (Array.isArray(res)) {
          permissions = res
        } else {
          console.error('未知的响应格式:', res)
          permissions = []
        }
        
        this.$set(this.rolePermissionsMap, roleId, permissions)
      } catch (err) {
        console.error('获取角色权限错误:', err)
        this.$set(this.rolePermissionsMap, roleId, [])
      }
    },
    
    // 获取角色的权限列表
    getRolePermissions(roleId) {
      return this.rolePermissionsMap[roleId] || []
    },
    
    // 获取按模块分组的权限（用于表格展开显示）
    getGroupedPermissionsByRole(roleId) {
      const permissions = this.getRolePermissions(roleId)
      const grouped = []
      const moduleMap = {}
      
      permissions.forEach(permission => {
        if (!moduleMap[permission.module]) {
          moduleMap[permission.module] = {
            module: permission.module,
            permissions: []
          }
          grouped.push(moduleMap[permission.module])
        }
        moduleMap[permission.module].permissions.push(permission)
      })
      
      // 按模块名称排序
      grouped.sort((a, b) => {
        return a.module.localeCompare(b.module, 'zh-CN')
      })
      
      return grouped
    },
    
    // 获取角色描述
    getRoleDesc(roleId) {
      const descMap = {
        1: '平台最高级管理员',
        2: '管理各类后台内容与操作',
        3: '负责活动、商品、订单等全流程管理',
        4: '负责订单、报名人员和推荐码的处理与维护',
        5: '查看订单和数据并可导出',
        6: '专注于负责活动相关的信息处理'
      }
      return descMap[roleId] || '-'
    },
    
    // 显示分配权限对话框
    async showAssignPermissionDialog(role) {
      if (!role) {
        return
      }
      
      this.currentRole = role
      
      // 获取当前角色的权限
      await this.fetchRolePermissions(role.id)
      
      // 设置已选中的权限ID
      this.selectedPermissionIds = this.getRolePermissions(role.id).map(p => p.id)
      
      this.showAssignDialog = true
    },
    
    // 保存角色权限
    async saveRolePermissions() {
      if (!this.currentRole) {
        return
      }
      
      try {
        const res = await this.$http({
          method: 'put',
          url: `roles/${this.currentRole.id}/permissions`,
          data: {
            permission_ids: this.selectedPermissionIds
          }
        })
        
        if (res.code !== 200 && !res.success) {
          return this.$message.error(res.message || '分配权限失败')
        }
        
        this.$message.success('分配权限成功')
        this.showAssignDialog = false
        
        // 重新获取该角色的权限
        await this.fetchRolePermissions(this.currentRole.id)
      } catch (err) {
        console.error('分配权限错误:', err)
        this.$message.error(err.response?.data?.message || err.message || '分配权限失败')
      }
    },
    
    // 重置分配权限表单
    resetAssignForm() {
      this.selectedPermissionIds = []
      this.currentRole = null
    },
    
    // 判断模块是否全选
    isCheckAll(module) {
      const modulePermissions = this.allPermissionsGrouped[module] || []
      if (modulePermissions.length === 0) return false
      return modulePermissions.every(p => this.selectedPermissionIds.includes(p.id))
    },
    
    // 判断模块是否半选
    isIndeterminate(module) {
      const modulePermissions = this.allPermissionsGrouped[module] || []
      if (modulePermissions.length === 0) return false
      const checkedCount = modulePermissions.filter(p => this.selectedPermissionIds.includes(p.id)).length
      return checkedCount > 0 && checkedCount < modulePermissions.length
    },
    
    // 处理全选/取消全选
    handleCheckAllChange(checked, module) {
      const modulePermissions = this.allPermissionsGrouped[module] || []
      if (modulePermissions.length === 0) return
      
      const modulePermissionIds = modulePermissions.map(p => p.id)
      const currentChecked = this.isCheckAll(module)
      
      // 如果当前是全选状态，点击后应该取消全选
      // 如果当前不是全选状态，点击后应该全选
      const shouldCheck = !currentChecked
      
      if (shouldCheck) {
        // 全选：添加该模块所有权限
        modulePermissionIds.forEach(id => {
          if (!this.selectedPermissionIds.includes(id)) {
            this.selectedPermissionIds.push(id)
          }
        })
      } else {
        // 取消全选：移除该模块所有权限
        this.selectedPermissionIds = this.selectedPermissionIds.filter(id => !modulePermissionIds.includes(id))
      }
    },
    
    // 从角色中移除权限
    async removePermissionFromRole(role, module, permissionId) {
      if (!role) {
        return
      }
      
      let message = ''
      let permissionIdsToRemove = []
      
      if (module) {
        // 移除整个模块的所有权限
        const modulePermissions = this.getGroupedPermissionsByRole(role.id).find(g => g.module === module)
        if (!modulePermissions || modulePermissions.permissions.length === 0) {
          return
        }
        permissionIdsToRemove = modulePermissions.permissions.map(p => p.id)
        message = `确定要移除「${module}」模块的所有权限吗？`
      } else if (permissionId) {
        // 移除单个权限
        permissionIdsToRemove = [permissionId]
        const permission = this.getRolePermissions(role.id).find(p => p.id === permissionId)
        message = `确定要移除权限「${permission ? permission.name : ''}」吗？`
      } else {
        return
      }
      
      try {
        await this.$confirm(message, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        // 获取当前角色的所有权限ID
        const currentPermissionIds = this.getRolePermissions(role.id).map(p => p.id)
        // 移除指定的权限ID
        const updatedPermissionIds = currentPermissionIds.filter(id => !permissionIdsToRemove.includes(id))
        
        // 调用API更新角色权限
        const res = await this.$http({
          method: 'put',
          url: `roles/${role.id}/permissions`,
          data: {
            permission_ids: updatedPermissionIds
          }
        })
        
        if (res.code !== 200 && !res.success) {
          return this.$message.error(res.message || '移除权限失败')
        }
        
        this.$message.success('移除权限成功')
        
        // 重新获取该角色的权限
        await this.fetchRolePermissions(role.id)
      } catch (err) {
        if (err !== 'cancel') {
          console.error('移除权限错误:', err)
          this.$message.error(err.response?.data?.message || err.message || '移除权限失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper .el-table {
  min-width: 100%;
}

/* 展开内容区域 */
.expand-content {
  padding: 24px 40px;
  background: #FAFAFA;
}

.permissions-container {
  padding: 0;
}

.permission-group {
  padding: 16px 0;
  border-bottom: 1px solid #EBEEF5;
}

.permission-group.first-group {
  padding-top: 0;
}

.permission-group:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.module-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.module-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 32px;
}

.module-tag {
  font-weight: 500;
  font-size: 13px;
  height: 28px;
  line-height: 26px;
  padding: 0 12px;
  margin: 0;
  margin-right: 10px;
  cursor: pointer;
}

.module-icon {
  color: #C0C4CC;
  font-size: 14px;
  vertical-align: middle;
}

.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.permission-tag {
  font-size: 12px;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  margin: 0;
  cursor: pointer;
}

.empty-state {
  padding: 40px 0;
}

.role-desc {
  color: #606266;
  font-size: 13px;
  line-height: 20px;
}

/* 分配权限对话框 */
.assign-dialog ::v-deep .el-dialog__body {
  padding: 15px 20px;
}

.dialog-header {
  margin: -10px 0 12px;
}

.dialog-header ::v-deep .el-alert {
  padding: 8px 12px;
}

.dialog-header ::v-deep .el-alert__title {
  font-size: 13px;
  line-height: 1.4;
}

.permissions-selector {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.permissions-selector::-webkit-scrollbar {
  width: 4px;
}

.permissions-selector::-webkit-scrollbar-track {
  background: #F5F7FA;
  border-radius: 3px;
}

.permissions-selector::-webkit-scrollbar-thumb {
  background: #DCDFE6;
  border-radius: 3px;
}

.permissions-selector::-webkit-scrollbar-thumb:hover {
  background: #C0C4CC;
}

.checkbox-group {
  width: 100%;
}

.module-section {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #FAFAFA;
  border-radius: 4px;
  border: 1px solid #EBEEF5;
}

.module-section:last-child {
  margin-bottom: 0;
}

.module-title {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #EBEEF5;
  height: 32px;
  display: flex;
  align-items: center;
}

.module-checkbox {
  display: flex;
  align-items: center;
  height: 100%;
}

.module-checkbox ::v-deep .el-checkbox__label {
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 24px;
  padding-left: 6px;
}

.module-label-tag {
  font-weight: 500;
  font-size: 12px;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  margin: 0;
  margin-right: 8px;
}

.permissions-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 24px;
  align-items: center;
}

.permission-checkbox {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 8px;
  background: #FFFFFF;
  border-radius: 6px;
  border: 1px solid #EBEEF5;
  white-space: nowrap;
}

.permission-checkbox{
  margin-right: 6px;
}

.permission-checkbox ::v-deep .el-checkbox__label {
  font-size: 12px;
  color: #606266;
  padding-left: 6px;
  line-height: 28px;
}

.permission-name {
  font-size: 12px;
  color: #606266;
  line-height: 28px;
}

.empty-permissions {
  padding: 40px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.dialog-footer ::v-deep .el-button {
  padding: 8px 16px;
  font-size: 13px;
}

@media (max-width: 768px) {
  .roles-container {
    padding: 0 5px;
  }

  .el-card {
    margin-top: 10px;
    padding: 10px;
  }

  .el-table {
    font-size: 11px;
    overflow-x: auto;
    display: block;
    -webkit-overflow-scrolling: touch;
  }

  .el-table ::v-deep .el-table__body-wrapper,
  .el-table ::v-deep .el-table__header-wrapper {
    overflow-x: auto;
  }

  .el-table th,
  .el-table td {
    padding: 8px 5px !important;
  }

  .el-table .el-button--small {
    font-size: 11px;
  }

  .expand-content {
    padding: 10px !important;
  }

  .module-row {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .module-header {
    height: auto;
    flex-wrap: wrap;
  }

  .permissions-list {
    flex-wrap: wrap;
    gap: 6px !important;
  }

  .permission-tag {
    font-size: 11px;
    height: 22px;
    line-height: 20px;
    padding: 0 8px;
  }

  .role-desc {
    font-size: 11px;
    line-height: 16px;
  }

  .permission-count-tag {
    font-size: 11px;
    padding: 2px 6px;
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
    top: 5vh;
  }

  .module-section {
    padding: 10px;
  }

  .permissions-grid {
    padding-left: 10px;
    gap: 4px;
  }

  .permission-checkbox {
    height: 26px;
    padding: 0 6px;
    font-size: 11px;
  }

  .permission-checkbox ::v-deep .el-checkbox__label {
    font-size: 11px;
  }
}
</style>